import React, { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import ReactTransition from 'react-addons-css-transition-group'
import { Button } from '@winnin/winnin_ui.components.button'
import { Carousel } from '@winnin/winnin_ui.components.carousel'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import BlockComponentsRole75Checker from 'components/BlockComponentsRole75Checker'
import { RCTooltip } from 'components/RCTooltip'
import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider/hook'
import { T, useTranslate } from 'lib/tolgee'
import { EditNicheModal } from 'modules/niche'
import { AudienceFiltersStore, FeatureFlagsStore, PanelStore, UserStore } from 'stores'
import { analyticsTracker, Filters, TOLGEE_GLOBALS } from 'utils'
import getCompareLimits from 'utils/getCompareLimits'
import { getRoute } from 'utils/routes'

import CardContentBar from './components/CardContentBar'
import RemoveFromJobModal from './components/RemoveFromJobModal'
import translations from './translations'
import './index.scss'

function SelectedItemsBar() {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isNicheModalOpen, setIsNicheModalOpen] = useState(false)

  const wrapperRef = useRef()
  const { resetItemsBar, selectedItemsBar, toggleModal } = useSelectedItemsList()
  const t = useTranslate()

  const isViewerUser = UserStore?.getRole === 'viewer'
  const isWinner = UserStore?.userInfo?.email?.includes('@winnin')
  const limits = getCompareLimits(isWinner)
  const isNicheEditingEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNicheEditingEnabled')

  const shouldRenderNicheButton = useMemo(() => {
    const isOnlyNiches = selectedItemsBar.every(item => item.type === 'niches')
    return isNicheEditingEnabled && isOnlyNiches
  }, [isNicheEditingEnabled, selectedItemsBar])

  const isGroupingNichesDisabled = useMemo(() => {
    return selectedItemsBar.length > 5
  }, [selectedItemsBar])

  const handleCompare = () => {
    if (selectedItemsBar.length <= 0) {
      return
    }

    const initialType = selectedItemsBar?.[0]?.type
    const type = initialType === 'creators' ? 'creators' : 'topics'
    const compareRoute = getRoute('/workspace/:panelId/compare/:tab?', {
      panelId: PanelStore?.selectedPanel?.panelId,
      tab: type,
    })
    const url = new URL(compareRoute, window.location.href)

    selectedItemsBar.forEach(item => {
      if (type === 'creators') {
        url.searchParams.append(item.type, `${item.provider}:${item.id}`)
      } else {
        url.searchParams.append(item.type, item.id)
      }
    })

    const audienceFilters = AudienceFiltersStore.audienceFilters
    const audienceFiltersQueryStringParameters = Filters.getFiltersQueryString(audienceFilters, 'audienceFilters')
    const hasAudienceFilters = !!audienceFiltersQueryStringParameters

    if (hasAudienceFilters) {
      const audienceFilters = new URLSearchParams(audienceFiltersQueryStringParameters)

      for (const p of audienceFilters) {
        url.searchParams.append(p?.[0], p?.[1])
      }
    }

    const entryPoint = selectedItemsBar.map(item => item?.name)
    const provider = selectedItemsBar?.[0]?.provider

    const { COMPARE } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(COMPARE, {
      'Compare Type': type,
      'Entry Point': entryPoint.join(', '),
      ...(provider && { Provider: provider }),
    })

    resetItemsBar()

    window.open(url, '_blank', 'noopener')
  }

  const removeFromJobWithFolder = () => {
    setIsDeleteModalOpen(true)
  }

  const handleRemove = () => {
    removeFromJobWithFolder()
    return
  }

  const handleNicheEdit = () => {
    const entryPoint = selectedItemsBar.map(item => item?.name)
    const { NICHE_EDITING } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(NICHE_EDITING.OPEN_MODAL, {
      Niches: entryPoint.join(', '),
    })
    setIsNicheModalOpen(true)
  }

  const disabledCompareMessage = useMemo(() => {
    const { providers, entityTypes } =
      selectedItemsBar?.reduce(
        (prev, curr) => {
          const topicsCompareTypes = [
            'topics',
            'subtopics',
            'savedPostSearches',
            'tags',
            'industry_topic',
            'global_topic',
          ]
          const currentType = topicsCompareTypes.includes(curr?.type) ? 'topics' : curr.type

          prev.entityTypes.push(currentType)

          if (currentType === 'creators' && curr.provider) {
            prev.providers.push(curr.provider)
          }

          const newProviders = new Set(prev.providers)
          const newEntityTypes = new Set(prev.entityTypes)

          return {
            providers: Array.from(newProviders),
            entityTypes: Array.from(newEntityTypes),
          }
        },
        {
          providers: [],
          entityTypes: [],
        }
      ) || {}

    if (entityTypes.length > 1) {
      return 'You can only compare one type of entity'
    }

    if (providers.length > 1) {
      return 'You can only compare things from the same platform'
    }

    if (entityTypes.includes('posts')) {
      return "You can't compare videos"
    }

    if (entityTypes.includes('trendings')) {
      return t(translations.trendingCompareErrorMessage)
    }

    if (entityTypes[0] === 'topics' && selectedItemsBar.length > limits.topics) {
      return `You can compare a max of ${limits.topics} topics, subtopics, tags or saved searches`
    }

    if (entityTypes[0] === 'creators' && selectedItemsBar.length > limits.creators) {
      return `You can compare a max of ${limits.creators} creators`
    }

    return null
  }, [selectedItemsBar, limits, t])

  const shouldRenderDeleteButton = useMemo(() => {
    const url = window.location.pathname
    const splitUrl = url.split('/')
    const isJobPage = splitUrl.includes('jobs') && splitUrl.includes('workspace') && !splitUrl.includes('dashboard')

    const hasPinId = selectedItemsBar.some(item => item?.pinId)
    return hasPinId && isJobPage && !isViewerUser

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewerUser, window.location.pathname])

  useEffect(() => {
    setTimeout(() => {
      document.body.style.marginBottom = selectedItemsBar?.length ? `${wrapperRef.current?.clientHeight}px` : 'unset'
    }, 200)
  }, [selectedItemsBar])

  const isCompareDisabled = Boolean(disabledCompareMessage)

  return (
    <ReactTransition
      transitionName="slide-from-bottom"
      component="div"
      className="SelectedItemsBar-transition"
      transitionEnterTimeout={0}
      transitionLeaveTimeout={0}
    >
      <div style={{ paddingTop: '3vh' }} />

      <div className="SelectedItemsBar-transition-wrapper">
        <div className="SelectedItemsBar-transition-wrapper-content" ref={wrapperRef}>
          <div className="SelectedItemsBar-transition-wrapper-content-items">
            <p>
              <T keyName="SelectedItemsBar-itemsSelectedLabel" parameters={{ count: selectedItemsBar?.length ?? 0 }}>
                {'{count, plural, one {# item} other {# items}} selected'}
              </T>
            </p>

            <ReactTransition
              transitionName={'slide-sideways'}
              transitionEnterTimeout={0}
              transitionLeaveTimeout={0}
              component="ul"
            >
              <Carousel className="carousel">
                {selectedItemsBar.map((item, index) => {
                  return <CardContentBar key={item.id} item={item} index={index} />
                })}
              </Carousel>
            </ReactTransition>
          </div>

          <div className="SelectedItemsBar-transition-wrapper-content-actions">
            <Button
              onClick={resetItemsBar}
              appearance="link"
              variation="faded"
              className="cancelButton manageItemsButton"
            >
              <T keyName={TOLGEE_GLOBALS.cancel.key}>{TOLGEE_GLOBALS.cancel.defaultValue}</T>
            </Button>

            {shouldRenderNicheButton && (
              <RCTooltip
                direction="top"
                tooltipMessage={t(translations.nicheEditLimit)}
                hideTooltip={!isGroupingNichesDisabled}
              >
                <Button appearance="link" className="manageItemsButton" onClick={handleNicheEdit}>
                  {t(translations.editNiche)}
                </Button>
              </RCTooltip>
            )}

            <TooltipWrapper isTooltipMultiline tooltipMessage={disabledCompareMessage} tooltipStyle="tooltippedN">
              <Button
                className="manageItemsButton"
                disabled={isCompareDisabled}
                onClick={handleCompare}
                icon="compare"
                appearance="link"
              >
                <T keyName="SelectedItemsBar-compareButton">Compare</T>
              </Button>
            </TooltipWrapper>

            <BlockComponentsRole75Checker tooltipPositionClassname="tooltippedN" isClickBlocked={isViewerUser}>
              {shouldRenderDeleteButton && (
                <Button
                  className="manageItemsButton"
                  onClick={handleRemove}
                  disabled={isViewerUser}
                  appearance="link"
                  icon="folderMinus"
                >
                  <T keyName="SelectedItemsBar-removeButton">Remove</T>
                </Button>
              )}

              <Button
                className="manageItemsButton"
                onClick={toggleModal}
                disabled={isViewerUser}
                appearance="link"
                icon="addTo"
              >
                <T keyName="SelectedItemsBar-addToDashboardButton">Add to dashboard</T>
              </Button>
            </BlockComponentsRole75Checker>
          </div>
        </div>
        {isNicheModalOpen && (
          <EditNicheModal handleClose={() => setIsNicheModalOpen(false)} isOpen={isNicheModalOpen} />
        )}
        {isDeleteModalOpen && (
          <RemoveFromJobModal isOpen={isDeleteModalOpen} closeModal={() => setIsDeleteModalOpen(false)} />
        )}
      </div>
    </ReactTransition>
  )
}

export default observer(SelectedItemsBar)
